import { busEvents } from "~/lib/bus-events";
import * as service from "./service";
import LStorage from "~/lib/storage";
import Logger from "~/lib/logger";
import {getInstantiatedWebsocketServicesNames, getService, getUninstantiatedWebsocketServicesNames} from "~/lib/services/services-manager";
import WsClient from "~/lib/services/ws-client";
import {User} from "~/lib/models/user";
import {isDefined} from "~/lib/helpers/guards";

const tokenKey = "token";
const sessionUuidKey = "sessionUuid";
const refreshTokenKey = "refreshToken";

let sessionExpirationDate = null;
let sessionExpirationCheckerInterval = null;

// TODO: extract sessionExpiration to module/auth/expiration.ts
const INACTIVITY_TIMEOUT_IN_SECONDS = 14400;
const INACTIVITY_CHECK_FREQUENCY = 2000;

const sessionExpirationListener = () => {
  sessionExpirationDate = new Date();
  sessionExpirationDate.setSeconds(sessionExpirationDate.getSeconds() + INACTIVITY_TIMEOUT_IN_SECONDS);
};

type AuthListenerCallbacks = {
  onLogin?: (event:CustomEvent) => void;
  onLogout?: () => void;
};

export function createAuthListener({ onLogin, onLogout }: AuthListenerCallbacks){
  if(onLogin) busEvents.on("auth.login", onLogin);
  if(onLogout) busEvents.on("auth.logout", onLogout);
}

const onLogout = async (store) => {
  store.dispatch('stopInnactivityCheck');
  store.commit("clearAuth");
  sendLogoutEvent();
  await stopWebsocketConnexion();
};

function isExpired(){
  return sessionExpirationDate instanceof Date && (sessionExpirationDate.getTime() - new Date().getTime() <= 0);
}

const checkSessionExpired = store => {
  if( isExpired()) {
    busEvents.send("auth.expired", {
      onDone: async () => await onLogout(store)
    });
  }
};

const startWebsocketConnexion = async (token) => {
  // Tous les services non instancié récupéré (only websocket)
  const services: WsClient[] = getUninstantiatedWebsocketServicesNames().map((srv) => getService(srv, { token }));

  services.forEach((srv) => {
    if (!srv.isConnected()) {
      srv.connect({ token });
    }
  });
};

const stopWebsocketConnexion = async () => {
  const services: WsClient[] = getInstantiatedWebsocketServicesNames().map((srv) => getService(srv));
  services.forEach((srv) => {
    srv.disconnect();
  });
};

export function sendLogoutEvent(){
  busEvents.send("auth.logout", {});
}

export function sendLoginEvent(sessionData){
  busEvents.send("auth.login", sessionData);
}

// TODO: decouple these commits to their own implementations
export const getSessionData = async (store, token, withSockets = true, sessionUuid, refreshToken) => {
  const session = await service.getSessionData(token);
  store.commit("authenticationSuccess", { token, session, sessionUuid, refreshToken });
  sendLoginEvent(session);
  withSockets && (await startWebsocketConnexion(token))
};

type AuthState = {
  user: User,
  token: string,
  isLoading: boolean,
  isErrored: boolean,
  error: any,
}

export default {
  state: {
    user: null,
    token: null,
    isLoading: false,
    isErrored: false,
    error:null,
    session: null,
    sessionUuid: null,
    refreshToken: null,
  } as AuthState,
  getters: {
    authToken: state => state.token,
    isAuthenticated: state => (acl?: boolean[] | boolean) => {
      if (!state.user) return false;

      if (typeof acl !== 'undefined') {
        if (acl === null) return false; // If undefined acl received => false
        try {
          return Array.isArray(acl) ? !acl.some((a) => !a) : acl;
        } catch (_) {
          return false;
        }
      }

      return true;
    },
    currentUser: (state): User => state.user,
    session: (state) => state.session,
    sessionUuid: state => state.sessionUuid,
  },
  mutations: {
    authenticationStart: state => {
      state.sessionExpirationDate = null;
      state.isLoading = true;
      state.isErrored = false;
      state.error = null;
      state.token = null;
      state.user = null;
      state.refreshToken = null;
    },
    authenticationSuccess: (state, { token, session, sessionUuid, refreshToken }) => {
      state.isLoading = false;
      state.isErrored = false;
      state.error = null;
      state.token = token;
      state.user = session.user;
      state.session = session;
      state.refreshToken = refreshToken;
      state.sessionUuid = sessionUuid;
      LStorage.set(tokenKey, token);
      LStorage.set(sessionUuidKey, sessionUuid);
      LStorage.set(refreshTokenKey, refreshToken);
    },
    authenticationError: (state, err) => {
      state.isLoading = false;
      state.isErrored = true;
      state.error = err;
    },
    clearAuth: state => {
      state.sessionExpirationDate = null;
      state.token = null;
      state.user = null;
      state.session = null;
      LStorage.clear(tokenKey);
      LStorage.clear(refreshTokenKey);
    },
    setSession: (state, session) => {
      state.session = session;
    },
    setAuthToken: (state, token) => {
      state.token = token;
    }
  },
  actions: {

    startInnactivityCheck: store => {

      if (sessionExpirationDate === null) {
        document.addEventListener("mouseup", sessionExpirationListener, true);
        sessionExpirationListener();
        sessionExpirationCheckerInterval = setInterval( () => checkSessionExpired(store), INACTIVITY_CHECK_FREQUENCY );
      }
    },

    stopInnactivityCheck: () => {
      if (sessionExpirationDate !== null) {
        document.removeEventListener("mouseup", sessionExpirationListener, true);
        sessionExpirationDate = null;
        clearInterval(sessionExpirationCheckerInterval);
      }
    },

    async logout(store) {
      await onLogout(store);
    },

    async authenticate(store, { email, password }) {
      store.commit("authenticationStart");
      try {
        const { token, sessionUuid, refreshToken } = await service.getToken({ email, password, sessionUuid: store.sessionUuid });
        await getSessionData(store, token, undefined, sessionUuid, refreshToken);
        store.dispatch("startInnactivityCheck");
      } catch (err) {
        store.commit("authenticationError", err);
        store.commit("clearAuth");
        await stopWebsocketConnexion();
      }
    },

    async restoreSession(store, inputToken) {

      let token = isDefined(inputToken) ? inputToken : LStorage.get(tokenKey);
      const sessionUuid = LStorage.get(sessionUuidKey);
      const refreshToken = LStorage.get(refreshTokenKey);

      if (token) {
        try {
          await service.authorize(token);
          await getSessionData(store, token, undefined, sessionUuid, refreshToken );
          store.dispatch("startInnactivityCheck");
        } catch (err) {
          Logger.error(err);
          store.commit("clearAuth");
          await stopWebsocketConnexion();
        }
      }
    }
  }
};
